<template>
  <div v-if="gasMeterExchange">
    <div class="heading">{{ meterDetails.gasMeterLocation.question }}</div>
    <SoRadioGroup
      v-model="meterDetails.gasMeterLocation.answer"
      :items="gasMeterLocationOptions"
      :rules="[rules.required]"
    />
  </div>
  <div v-if="isDualFuelSite">
    <div class="heading">
      {{ meterDetails.distanceBetweenMeters.question }}
    </div>
    <SoRadioGroup
      v-model="meterDetails.distanceBetweenMeters.answer"
      :items="distanceBetweenMetersOptions"
      :rules="[rules.required]"
    />
  </div>
  <div class="heading">Check with the customer if:</div>
  <SoCheckbox
    v-model="meterDetails.meterAboveHeadHeight.answer"
    :label="meterDetails.meterAboveHeadHeight.question"
  />
  <SoCheckbox
    v-model="meterDetails.lessThanSixInchesAroundMeter.answer"
    :label="meterDetails.lessThanSixInchesAroundMeter.question"
  />
  <div class="heading mt-6">
    Record only if the customer mentions one of the below by themselves:
  </div>
  <SoCheckbox
    v-if="electricMeterExchange"
    v-model="meterDetails.electricityMeterThreePhase.answer"
    :label="meterDetails.electricityMeterThreePhase.question"
    :rules="[elecMeterExclusivityRule]"
    @update:model-value="onElecCheckboxChange('threePhase')"
  />
  <SoCheckbox
    v-if="electricMeterExchange"
    v-model="meterDetails.electricityMeterFiveTerminal.answer"
    :label="meterDetails.electricityMeterFiveTerminal.question"
    :rules="[elecMeterExclusivityRule]"
    @update:model-value="onElecCheckboxChange('fiveTerminal')"
  />
  <SoAlert v-if="showElecMeterWarning">
    An electricity meter cannot be both 3-phase and 5-terminal
  </SoAlert>
  <SoCheckbox
    v-if="gasMeterExchange"
    v-model="meterDetails.gasMeterMediumPressureOrCommercial.answer"
    :label="meterDetails.gasMeterMediumPressureOrCommercial.question"
  />
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoRadioGroup from "@/components/formControls/SoRadioGroup.vue"
  import SoCheckbox from "@/components/formControls/SoCheckbox.vue"
  import rules from "@/validators/rules"
  import { ImbpQuestionnaire } from "@/flows/useCases/imbpBookingNew/types"
  import { ISmartMeterEligibility } from "@/models/smartMeterEligibility/smartMeterEligibility.types"
  import { METER_TYPE, SMEX_JOB_NOTES } from "@/constants/imbp"

  export default defineComponent({
    components: {
      SoAlert,
      SoRadioGroup,
      SoCheckbox,
    },
    props: {
      modelValue: {
        type: Object,
        default: () => ({}),
      },
      hasElectricMeter: {
        type: Boolean,
        default: false,
      },
      hasGasMeter: {
        type: Boolean,
        default: false,
      },
      eligibility: {
        type: Object as PropType<ISmartMeterEligibility>,
        default: null,
      },
      onSupplyMeterTypes: {
        type: Array as PropType<string[]>,
        default: () => [],
      },
      onSupplyGasMeterType: {
        type: String as PropType<string | null>,
        default: null,
      },
      onSupplyElecMeterType: {
        type: String as PropType<string | null>,
        default: null,
      },
    },
    emits: ["update:modelValue"],
    data() {
      return {
        meterDetails: {
          note: {
            question: "Important to note re the job?",
            answer: "",
          },
          gasMeterLocation: {
            question: "Location of gas meter",
            answer: null,
          },
          distanceBetweenMeters: {
            question:
              "Distance between gas meter and electricity meter (estimated in meters or feet)",
            answer: null,
          },
          meterAboveHeadHeight: {
            question:
              "Meter is above head height (engineer will bring a ladder if so)",
            answer: false,
          },
          lessThanSixInchesAroundMeter: {
            question: "Less than 6 inches of space around one or more meters",
            answer: false,
          },
          electricityMeterThreePhase: {
            question: "Electricity meter is three-phase",
            answer: false,
          },
          electricityMeterFiveTerminal: {
            question: "Electricity meter is five-terminal",
            answer: false,
          },
          gasMeterMediumPressureOrCommercial: {
            question: "Gas meter is large, medium-pressure, or commercial",
            answer: false,
          },
        } satisfies ImbpQuestionnaire,
        showElecMeterWarning: false,
        gasMeterLocationOptions: [
          { text: "Inside the property", value: "Inside" },
          { text: "Outside the property", value: "Outside" },
        ],
        distanceBetweenMetersOptions: [
          { text: "0-5m / 0-15ft", value: "Less than 5m / 15ft" },
          { text: "5-10m / 16-32ft", value: "5-10m / 16-32ft" },
          { text: "Further than 10m / 32ft", value: "More than 10m / 32ft" },
        ],
        rules: rules,
      }
    },
    computed: {
      elecMeterExclusivityRule() {
        const bothChecked =
          this.meterDetails.electricityMeterThreePhase.answer &&
          this.meterDetails.electricityMeterFiveTerminal.answer
        return () =>
          bothChecked
            ? "An electricity meter cannot be both 3-phase and 5-terminal"
            : true
      },
      isDualFuelSite() {
        return (
          (this.gasMeterExchange && this.hasElectricMeter) ||
          (this.electricMeterExchange && this.hasGasMeter)
        )
      },
      gasMeterExchange() {
        return this.eligibility.eligibleForGasExchange
      },
      electricMeterExchange() {
        return this.eligibility.eligibleForElectricExchange
      },
      meterTypeKey() {
        if (this.isDualFuelSite) {
          if (this.gasMeterExchange && !this.electricMeterExchange) {
            return this.s2Key(this.onSupplyGasMeterType, "DUAL_GAS_ONLY")
          }

          if (this.electricMeterExchange && !this.gasMeterExchange) {
            return this.s2Key(this.onSupplyElecMeterType, "DUAL_ELEC_ONLY")
          }

          return `DUAL_${this.onSupplyMeterTypes.join("-")}`
        }

        if (this.gasMeterExchange) {
          return this.s2Key(this.onSupplyGasMeterType, "SINGLE_GAS")
        }

        if (this.electricMeterExchange) {
          return this.s2Key(this.onSupplyElecMeterType, "SINGLE_ELEC")
        }

        return ""
      },
      updateNote() {
        return SMEX_JOB_NOTES[this.meterTypeKey] ?? ""
      },
    },
    watch: {
      updateNote: {
        handler(note) {
          if (note) {
            this.meterDetails.note.answer = note
            this.$emit("update:modelValue", this.meterDetails)
          }
        },
        immediate: true,
      },
      meterDetails: {
        handler(newValue) {
          this.$emit("update:modelValue", newValue)
        },
        deep: true,
      },
    },
    methods: {
      s2Key(meterType: string | null, baseKey: string) {
        return meterType === METER_TYPE.SMETS2 ? `${baseKey}_SMETS2` : baseKey
      },
      onElecCheckboxChange(changed: "threePhase" | "fiveTerminal") {
        const threePhase = this.meterDetails.electricityMeterThreePhase.answer
        const fiveTerminal =
          this.meterDetails.electricityMeterFiveTerminal.answer

        if (changed === "threePhase" && threePhase && fiveTerminal) {
          this.meterDetails.electricityMeterFiveTerminal.answer = false
          this.showElecMeterWarning = true
        } else if (changed === "fiveTerminal" && fiveTerminal && threePhase) {
          this.meterDetails.electricityMeterThreePhase.answer = false
          this.showElecMeterWarning = true
        } else if (!threePhase && !fiveTerminal) {
          this.showElecMeterWarning = false
        }
      },
    },
  })
</script>

<style scoped>
  .heading {
    font-size: 18px;
    font-weight: 500;
    margin-bottom: 20px;
  }

  :deep(.radio-label-container span) {
    font-size: 18px;
  }

  :deep(.v-messages__message) {
    margin: 5px 0 35px -5px;
  }
</style>
